/**
 * Detecta números telefónicos en diferentes formatos
 * @param {string} text - Texto a validar
 * @returns {boolean} - true si encuentra números telefónicos
 */
export const detectPhoneNumbers = (text) => {
	const phonePatterns = [
		/\b\d{5,}\b/g,          // 5 o más dígitos seguidos
		/\+591\s*\d{8}/g,       // +591 seguido de 8 dígitos
		/591\s*\d{8}/g,         // 591 seguido de 8 dígitos
		/\b[67]\d{7}\b/g,       // Números móviles que empiecen con 6 o 7
		/\d{3}[-\s]?\d{4}/g,    // Patrones con guiones o espacios (123-4567)
		/\(\d{3}\)\s*\d{4}/g,   // Patrones con paréntesis (123) 4567
	];

	return phonePatterns.some(pattern => pattern.test(text));
};

/**
 * Detecta direcciones de correo electrónico y patrones relacionados
 * @param {string} text - Texto a validar
 * @returns {boolean} - true si encuentra emails o patrones sospechosos
 */
export const detectEmails = (text) => {
	// Patrón tradicional de email
	const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

	// Patrones de email ofuscados o alternativos
	const obfuscatedPatterns = [
		/[a-zA-Z0-9._%+-]+\s*@\s*[a-zA-Z0-9.-]+\s*\.\s*[a-zA-Z]{2,}/g, // espacios
		/[a-zA-Z0-9._%+-]+\s*\(\s*@\s*\)\s*[a-zA-Z0-9.-]+/g, // @ entre paréntesis
		/[a-zA-Z0-9._%+-]+\s*\[at\]\s*[a-zA-Z0-9.-]+/gi, // [at] en lugar de @
		/[a-zA-Z0-9._%+-]+\s*arroba\s*[a-zA-Z0-9.-]+/gi, // "arroba" en español
	];

	return emailPattern.test(text) || obfuscatedPatterns.some(pattern => pattern.test(text));
};

/**
 * Detecta secuencias largas de números (4 o más dígitos)
 * @param {string} text - Texto a validar
 * @returns {boolean} - true si encuentra secuencias largas
 */
export const hasLongNumberSequence = (text) => {
	const numberSequences = text.match(/\d+/g);
	if (numberSequences) {
		return numberSequences.some(seq => seq.length >= 5);
	}
	return false;
};

/**
 * Detecta caracteres sospechosos que podrían ser usados para emails
 * @param {string} text - Texto a validar
 * @returns {boolean} - true si encuentra caracteres sospechosos
 */
export const detectSuspiciousChars = (text) => {
	// Detecta @ seguido de caracteres que podrían formar un dominio
	const atSymbolPattern = /@\s*[a-zA-Z0-9.-]/g;

	// Detecta patrones como "dot com", "punto com", etc.
	const dotPattern = /\b(dot|punto|point)\s*(com|org|net|edu|gov|co|es|bo)\b/gi;

	// Detecta ".*" que podría ser usado para ocultar dominios
	const asteriskPattern = /\.\*/g;

	return atSymbolPattern.test(text) || dotPattern.test(text) || asteriskPattern.test(text);
};

/**
 * Validación principal que combina todas las validaciones
 * @param {string} text - Texto a validar
 * @returns {object} - Objeto con resultado de validación
 */
export const validateChatMessage = (text) => {
	if (!text.trim()) {
		return {
			isValid: true,
			isBlocked: false,
			errorMessage: '',
			violations: []
		};
	}

	const hasPhone = detectPhoneNumbers(text);
	const hasEmail = detectEmails(text);
	const hasSuspiciousChars = detectSuspiciousChars(text);
	const hasLongNumbers = hasLongNumberSequence(text);

	const violations = [];
	if (hasPhone) violations.push('números telefónicos');
	if (hasEmail) violations.push('direcciones de correo');
	if (hasSuspiciousChars && !hasEmail) violations.push('caracteres sospechosos de emails');
	if (hasLongNumbers && !hasPhone) violations.push('secuencias de números');

	const isBlocked = violations.length > 0;

	return {
		isValid: !isBlocked,
		isBlocked,
		errorMessage: isBlocked
			? `⚠️ No está permitido enviar ${violations.join(' ni ')}. El incumplimiento de esta norma puede resultar en un ban de la plataforma.`
			: '',
		violations
	};
};

/**
 * Validación principal que verifica si un mensaje contiene contenido prohibido
 * @param {string} content - Contenido del mensaje
 * @returns {boolean} - true si el mensaje contiene contenido prohibido
 */
export const hasProhibitedContent = (content) => {
	if (!content || typeof content !== 'string') {
		return false;
	}

	const hasPhone = detectPhoneNumbers(content);
	const hasEmail = detectEmails(content);
	const hasSuspiciousChars = detectSuspiciousChars(content);
	const hasLongNumbers = hasLongNumberSequence(content);

	return hasPhone || hasEmail || hasSuspiciousChars || hasLongNumbers;
};

/**
 * Procesa un mensaje individual, reemplazándolo si contiene contenido prohibido
 * @param {Object} threadItem - Item del thread con contenido
 * @param {string} replacementMessage - Mensaje de reemplazo
 * @returns {Object} - ThreadItem procesado
 */
export const processThreadItem = (threadItem, replacementMessage = "Mensaje borrado por infracción de normas eslipin.com") => {
	if (!threadItem || !threadItem.content) {
		return threadItem;
	}

	if (hasProhibitedContent(threadItem.content)) {
		threadItem.content = replacementMessage;
		// Opcional: agregar un flag para identificar mensajes reemplazados
		//processedItem.isReplaced = true;
		//processedItem.originalContentLength = threadItem.content.length;
	}

	return threadItem;
};

/**
 * Filtra completamente los mensajes que contienen contenido prohibido
 * @param {Array} threadItems - Array de items del thread
 * @returns {Array} - Array filtrado sin mensajes prohibidos
 */
export const filterProhibitedMessages = (threadItems) => {
	if (!Array.isArray(threadItems)) {
		return threadItems;
	}

	return threadItems.filter(item => {
		if (!item || !item.content) {
			return true; // Mantener items sin contenido
		}
		return !hasProhibitedContent(item.content);
	});
};

/**
 * Reemplaza el contenido de mensajes prohibidos con un mensaje personalizado
 * @param {Array} threadItems - Array de items del thread
 * @param {string} replacementMessage - Mensaje de reemplazo
 * @returns {Array} - Array con mensajes prohibidos reemplazados
 */
export const replaceProhibitedMessages = (threadItems, replacementMessage = "Mensaje borrado por infracción de normas eslipin.com") => {
	if (!Array.isArray(threadItems)) {
		return threadItems;
	}

	return threadItems.map(item => processThreadItem(item, replacementMessage));
};
